// Based on react-quick-pinch-zoom. This package does not work on NextJS
// https://github.com/retyui/react-quick-pinch-zoom

import _ from 'lodash';
import hotkeys from 'hotkeys-js';
import useEvent from 'react-use-v13/lib/useEvent';
import React, { useRef, useCallback, useLayoutEffect, useImperativeHandle } from 'react';

import useShortcut from 'react-scale/hooks/useShortcut';
import useMountedRef from 'react-scale/hooks/useMountedRef';

export interface PinchContainerImperativeApi {
  reset(): void;
  zoomTo(selector: string, proportion?: number): void;
}

interface Point {
  x: number;
  y: number;
}

export interface ZoomValue {
  x: number;
  y: number;
  scale: number;
}

const getElementSize = (element: HTMLElement | null): { width: number; height: number } => {
  if (element) {
    const { offsetWidth, offsetHeight } = element;

    // Any DOMElement
    if (offsetWidth && offsetHeight) {
      return { width: offsetWidth, height: offsetHeight };
    }

    // Svg support
    const style = getComputedStyle(element);
    const width = parseFloat(style.width);
    const height = parseFloat(style.height);

    if (height && width) return { width, height };
  }

  return { width: 0, height: 0 };
};

const getEventOffset = (element: HTMLElement | null, event: MouseEvent | WheelEvent): Point => {
  if (!element) return { x: 0, y: 0 };

  const _body = window.document.body;
  const _html = window.document.documentElement;

  const { top, left } = element.getBoundingClientRect();
  const scrollTop = _html.scrollTop || _body.scrollTop;
  const scrollLeft = _html.scrollLeft || _body.scrollLeft;
  const posTop = top + scrollTop;
  const posLeft = left + scrollLeft;

  return {
    x: event.pageX - posLeft + element.scrollLeft,
    y: event.pageY - posTop + element.scrollTop,
  };
};

interface PinchContainerProps {
  minZoom?: number;
  maxZoom?: number;
  canDrag?: boolean;
  style?: React.CSSProperties;
  enableNavigation?: boolean;
  onUpdate: (zoom: ZoomValue) => void;
}

const PinchContainer: React.ForwardRefRenderFunction<
  PinchContainerImperativeApi,
  React.PropsWithChildren<PinchContainerProps>
> = (
  { style, minZoom = 1, maxZoom = 50, canDrag = true, enableNavigation, onUpdate, children },
  forwardRef,
) => {
  const ref = useMountedRef();

  const zoomFactor = useRef<number>(1);

  const isDragging = useRef<boolean>(false);

  const offset = useRef<Point>({ x: 0, y: 0 });

  const dragStartPosition = useRef<Point | null>(null);

  const wrapperSize = useRef<DOMRectReadOnly | null>(null);

  const update = useCallback(() => {
    const scale = zoomFactor.current;
    const x = -offset.current.x / scale;
    const y = -offset.current.y / scale;

    onUpdate({ scale, x, y });
  }, [onUpdate]);

  const sanitizeOffset = useCallback(
    (point: Point): Point => {
      const rect = wrapperSize.current;

      if (!ref.current || !rect) return { x: 0, y: 0 };

      const child = ref.current.firstElementChild;
      const { width, height } = getElementSize(child as HTMLElement);
      const elWidth = width * zoomFactor.current;
      const elHeight = height * zoomFactor.current;
      const maxX = elWidth - rect.width;
      const maxY = elHeight - rect.height;

      return {
        x: _.clamp(point.x, Math.min(maxX, 0), Math.max(maxX, 0)),
        y: _.clamp(point.y, Math.min(maxY, 0), Math.max(maxY, 0)),
      };
    },
    [ref],
  );

  const addOffset = useCallback((point: Point) => {
    offset.current = {
      x: offset.current.x + point.x,
      y: offset.current.y + point.y,
    };
  }, []);

  const scaleTo = (factor: number, center: Point) => {
    const originalZoomFactor = zoomFactor.current;

    zoomFactor.current *= factor / zoomFactor.current;
    zoomFactor.current = _.clamp(zoomFactor.current, minZoom, maxZoom);

    addOffset({
      x: (zoomFactor.current / originalZoomFactor - 1) * (center.x + offset.current.x),
      y: (zoomFactor.current / originalZoomFactor - 1) * (center.y + offset.current.y),
    });
  };

  const sanitize = useCallback(() => {
    const sOffset = sanitizeOffset(offset.current);
    const _isInsaneOffset = sOffset.x !== offset.current.x || sOffset.y !== offset.current.y;

    if (!_isInsaneOffset) return;

    const x = offset.current.x + (sOffset.x - offset.current.x);
    const y = offset.current.y + (sOffset.y - offset.current.y);

    offset.current = { x, y };

    update();
  }, [sanitizeOffset, update]);

  const handleMouseUp = () => {
    isDragging.current = false;
  };

  const handleMouseDown = (mouseEvent: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    if (!canDrag) return;

    mouseEvent.preventDefault();

    isDragging.current = true;

    dragStartPosition.current = null;
  };

  const handleMouseMove = (mouseEvent: MouseEvent) => {
    if (!isDragging.current) return;

    mouseEvent.stopPropagation();
    mouseEvent.preventDefault();

    const touch = getEventOffset(ref.current, mouseEvent);

    if (dragStartPosition.current) {
      const y = -(touch.y - dragStartPosition.current.y);
      const x = -(touch.x - dragStartPosition.current.x);

      addOffset({ x, y });
    }

    offset.current = sanitizeOffset(offset.current);

    dragStartPosition.current = touch;

    update();
  };

  const performChange = (scale: number, center: Point) => {
    scaleTo(scale, center);
    update();
    sanitize();
  };

  const handleWheelChange = (wheelEvent: WheelEvent) => {
    wheelEvent.stopPropagation();
    wheelEvent.preventDefault();

    const center = getEventOffset(ref.current, wheelEvent);
    const scale = zoomFactor.current - wheelEvent.deltaY * 0.005;

    performChange(scale, center);
  };

  const navigate = useCallback(() => {
    let x = 0;
    let y = 0;

    if (hotkeys.isPressed('a')) x -= 10;
    if (hotkeys.isPressed('w')) y -= 10;
    if (hotkeys.isPressed('d')) x += 10;
    if (hotkeys.isPressed('s')) y += 10;

    addOffset({ x, y });
    offset.current = sanitizeOffset(offset.current);
    update();
  }, [addOffset, sanitizeOffset, update]);

  const zoomTo = useCallback(
    (selector: string, proportion: number = 0.65) => {
      const off = offset.current;
      const target = ref.current?.querySelector(selector);

      if (!target || !ref.current) return;

      const tBox = target.getBoundingClientRect();
      const pBox = ref.current.getBoundingClientRect();

      const scaleX = (pBox.width * zoomFactor.current * proportion) / tBox.width;
      const scaleY = (pBox.height * zoomFactor.current * proportion) / tBox.height;
      const scale = Math.min(scaleX, scaleY);
      const factor = scale / zoomFactor.current;

      const x = (tBox.x - pBox.x + off.x + tBox.width / 2) * factor - pBox.width / 2;
      const y = (tBox.y - pBox.y + off.y + tBox.height / 2) * factor - pBox.height / 2;

      offset.current = { x, y };
      zoomFactor.current = scale;

      onUpdate({ scale, x: -x / scale, y: -y / scale });
      sanitize();
    },
    [onUpdate, sanitize, ref],
  );

  const reset = useCallback(() => {
    offset.current = { x: 0, y: 0 };
    zoomFactor.current = 1;

    update();
    sanitize();
  }, [update, sanitize]);

  useEvent('mouseup', handleMouseUp);

  useEvent('mousemove', handleMouseMove);

  useEvent('wheel', handleWheelChange, ref.current, { passive: false });

  useShortcut({ trigger: '*', disabled: !enableNavigation, action: navigate });

  useImperativeHandle(forwardRef, (): PinchContainerImperativeApi => ({ reset, zoomTo }));

  useLayoutEffect(() => {
    if (!ref.current) return;

    // @ts-ignore
    const observer = new ResizeObserver(entries => {
      wrapperSize.current = entries?.[0].contentRect;
    });

    observer.observe(ref.current);

    return () => observer.disconnect();
  }, [addOffset, sanitize, update, ref]);

  return (
    <div className="wrapper" ref={ref} style={style} onMouseDown={handleMouseDown}>
      {children}

      <style jsx>{`
        .wrapper {
          width: 100%;
          height: 100%;
          overflow: hidden;
          touch-action: none;
        }
      `}</style>
    </div>
  );
};

export default React.forwardRef(PinchContainer);
